// 19-oct-2026 · Control de pagos de apartados (P5): el comprobante se prepara EN EL TELÉFONO.
//
// Por qué aquí y no solo en el servidor:
// - Una foto del banco sale de 3-6 MB; por datos móviles tardaba y a veces no llegaba. Comprimida
//   en el teléfono queda en 80-250 KB y el servidor (comprobante_img.js del bot) igual la revisa.
// - sha256_original se saca de los bytes TAL CUAL los eligió la vendedora: con eso el libro detecta
//   que el mismo comprobante ya se mandó a ese folio (o a otro) aunque se comprima distinto.
// - El PDF no se toca (el navegador no lo puede comprimir); solo se revisa que quepa. El tope es 5 MB
//   porque el de 3 MB de Pedidos web (App-merged.jsx:502) rechazaba PDF reales de 3.76 MB.
// - HEIC fuera de Safari no se puede leer: se pide una captura de pantalla.

const MB = 1024 * 1024;
export const TOPE_PDF = 5 * MB;
const LADO = 1600;          // lado mayor normal
const LADO_LARGA = 2200;    // captura de app bancaria (alto > 2 × ancho): si no, no se lee la letra
const LADO_CHICO = 1280;    // último recurso si a calidad 0.6 sigue pasando de 600 KB
const MINI = 320;

// ── SHA-256 ──
// crypto.subtle solo existe en contexto seguro (https o localhost). Si no está, se usa la versión
// en JS para no mandar un comprobante sin su huella (sin huella no hay aviso de duplicado).
const K256 = [
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
];
const aHex = (u8) => Array.prototype.map.call(u8, b => (b < 16 ? '0' : '') + b.toString(16)).join('');
export function sha256Js(bytes) {
  const l = bytes.length;
  const n = Math.ceil((l + 9) / 64) * 64;
  const m = new Uint8Array(n);
  m.set(bytes); m[l] = 0x80;
  const dv = new DataView(m.buffer);
  dv.setUint32(n - 8, Math.floor(l / 0x20000000));
  dv.setUint32(n - 4, (l * 8) % 4294967296);
  const H = [0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19];
  const W = new Array(64);
  const rot = (x, k) => (x >>> k) | (x << (32 - k));
  for (let i = 0; i < n; i += 64) {
    for (let t = 0; t < 16; t++) W[t] = dv.getUint32(i + t * 4);
    for (let t = 16; t < 64; t++) {
      const s0 = rot(W[t - 15], 7) ^ rot(W[t - 15], 18) ^ (W[t - 15] >>> 3);
      const s1 = rot(W[t - 2], 17) ^ rot(W[t - 2], 19) ^ (W[t - 2] >>> 10);
      W[t] = (W[t - 16] + s0 + W[t - 7] + s1) >>> 0;
    }
    let [a, b, c, d, e, f, g, h] = H;
    for (let t = 0; t < 64; t++) {
      const t1 = (h + (rot(e, 6) ^ rot(e, 11) ^ rot(e, 25)) + ((e & f) ^ (~e & g)) + K256[t] + W[t]) >>> 0;
      const t2 = ((rot(a, 2) ^ rot(a, 13) ^ rot(a, 22)) + ((a & b) ^ (a & c) ^ (b & c))) >>> 0;
      h = g; g = f; f = e; e = (d + t1) >>> 0; d = c; c = b; b = a; a = (t1 + t2) >>> 0;
    }
    H[0] = (H[0] + a) >>> 0; H[1] = (H[1] + b) >>> 0; H[2] = (H[2] + c) >>> 0; H[3] = (H[3] + d) >>> 0;
    H[4] = (H[4] + e) >>> 0; H[5] = (H[5] + f) >>> 0; H[6] = (H[6] + g) >>> 0; H[7] = (H[7] + h) >>> 0;
  }
  const out = new Uint8Array(32);
  const odv = new DataView(out.buffer);
  H.forEach((v, i) => odv.setUint32(i * 4, v));
  return aHex(out);
}
export async function sha256Hex(buf) {
  try {
    const sub = typeof window !== 'undefined' && window.crypto && window.crypto.subtle;
    if (sub && sub.digest) return aHex(new Uint8Array(await sub.digest('SHA-256', buf)));
  } catch (_) { /* sin contexto seguro: sigue la versión en JS */ }
  return sha256Js(new Uint8Array(buf));
}

// ArrayBuffer → base64 por pedazos (String.fromCharCode con millones de argumentos truena).
export function bufABase64(buf) {
  const u8 = new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < u8.length; i += 0x8000) s += String.fromCharCode.apply(null, u8.subarray(i, i + 0x8000));
  return btoa(s);
}
const bytesDeB64 = (b64) => Math.floor(b64.length * 3 / 4) - (b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0);

// Tipo por los PRIMEROS BYTES, no por el nombre ni por lo que diga el teléfono.
function tipoPorBytes(u8) {
  if (u8.length >= 4 && u8[0] === 0x25 && u8[1] === 0x50 && u8[2] === 0x44 && u8[3] === 0x46) return 'pdf';   // %PDF
  if (u8.length >= 3 && u8[0] === 0xFF && u8[1] === 0xD8 && u8[2] === 0xFF) return 'jpeg';
  if (u8.length >= 4 && u8[0] === 0x89 && u8[1] === 0x50 && u8[2] === 0x4E && u8[3] === 0x47) return 'png';
  return '';
}

function cargarImagen(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('No pude leer esa imagen. Manda una captura de pantalla del comprobante.')); };
    img.src = url;
  });
}

// Dibuja la imagen en un lienzo blanco (los PNG transparentes salían negros en JPEG) y la devuelve
// como base64 JPEG, sin agrandarla nunca.
function aJpeg(img, ladoMax, calidad) {
  const w0 = img.naturalWidth || img.width, h0 = img.naturalHeight || img.height;
  const k = Math.min(1, ladoMax / Math.max(w0, h0));
  const w = Math.max(1, Math.round(w0 * k)), h = Math.max(1, Math.round(h0 * k));
  const cv = document.createElement('canvas');
  cv.width = w; cv.height = h;
  const cx = cv.getContext('2d');
  cx.fillStyle = '#FFFFFF';
  cx.fillRect(0, 0, w, h);
  cx.drawImage(img, 0, 0, w, h);
  const b64 = cv.toDataURL('image/jpeg', calidad).split(',')[1] || '';
  cv.width = 1; cv.height = 1;   // iPhone: suelta la memoria del lienzo enseguida
  return { b64, bytes: bytesDeB64(b64), w, h };
}

// comprimirComprobante(file) → { b64, mime, bytes, mini_b64, sha256_original }
// Lanza Error con un texto para la vendedora si no se puede usar.
export async function comprimirComprobante(file) {
  if (!file) throw new Error('No se eligió ningún archivo.');
  const buf = await file.arrayBuffer();
  const sha256_original = await sha256Hex(buf);
  const u8 = new Uint8Array(buf, 0, Math.min(16, buf.byteLength));
  const tipo = tipoPorBytes(u8);
  const parecePdf = /pdf$/i.test(file.type || '') || /\.pdf$/i.test(file.name || '');
  if (tipo === 'pdf' || (parecePdf && !tipo)) {
    if (tipo !== 'pdf') throw new Error('Ese archivo no es un PDF válido. Manda una captura de pantalla del comprobante.');
    if (buf.byteLength > TOPE_PDF) {
      throw new Error('Ese PDF pesa ' + (buf.byteLength / MB).toFixed(1) + ' MB y el tope es 5 MB. Mándalo como captura de pantalla.');
    }
    return { b64: bufABase64(buf), mime: 'application/pdf', bytes: buf.byteLength, mini_b64: null, sha256_original };
  }
  const img = await cargarImagen(file);
  const w0 = img.naturalWidth || img.width, h0 = img.naturalHeight || img.height;
  if (!w0 || !h0) throw new Error('No pude leer esa imagen. Manda una captura de pantalla del comprobante.');
  const lado = h0 > 2 * w0 ? LADO_LARGA : LADO;
  let r = aJpeg(img, lado, 0.72);
  if (r.bytes > 400 * 1024) r = aJpeg(img, lado, 0.6);
  if (r.bytes > 600 * 1024) r = aJpeg(img, LADO_CHICO, 0.6);
  const mini = aJpeg(img, MINI, 0.6);
  return { b64: r.b64, mime: 'image/jpeg', bytes: r.bytes, mini_b64: mini.b64, sha256_original, ancho: r.w, alto: r.h };
}

// verArchivo(url) → { src, mime, liberar() }
// Los archivos viven bajo /api (el service worker deja /api solo a la red) y piden el token.
// NUNCA window.open(url): así se abre sin token y da 401 (es lo que le pasa a "Ver comprobante"
// de Pedidos web). Se baja con el token, se vuelve blob y se muestra; liberar() al cerrar.
export async function verArchivo(url) {
  let token = '';
  try { token = localStorage.getItem('token') || ''; } catch (_) {}
  const res = await fetch(url, { headers: token ? { Authorization: 'Bearer ' + token } : {} });
  if (!res.ok) {
    let msg = 'HTTP ' + res.status;
    try { const j = await res.json(); if (j && j.error) msg = j.error; } catch (_) {}
    const e = new Error(msg); e.status = res.status; throw e;
  }
  const blob = await res.blob();
  const src = URL.createObjectURL(blob);
  let libre = false;
  return { src, mime: blob.type || res.headers.get('Content-Type') || '', liberar: () => { if (!libre) { libre = true; URL.revokeObjectURL(src); } } };
}
